/** Central SEO + AEO (answer-engine) config for the public marketing site. */

export const SITE_URL = "https://aivoryems.com";
export const SITE_NAME = "Aivory EMS";
export const OG_IMAGE = `${SITE_URL}/og/aivory-ems-social.png`;

export const DEFAULT_SEO = {
  title: "Event Management Software Singapore | Registration, Check-in & AI | Aivory EMS",
  description:
    "Aivory EMS is Singapore-hosted event management software for registration, QR check-in, badge printing, email campaigns, and AivoryONE AI co-planner. Annual plans from SGD 1,188. No per-ticket platform fee.",
  keywords: [
    "event management software",
    "event management software Singapore",
    "event registration software",
    "conference management platform",
    "onsite check-in software",
    "badge printing events",
    "hybrid event management",
    "event management platform APAC",
    "AI event planning",
  ].join(", "),
};

function upsertMeta(attr, key, value) {
  if (!value) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function removeJsonLd(id) {
  document.getElementById(id)?.remove();
}

function injectJsonLd(id, data) {
  removeJsonLd(id);
  if (!data) return;
  const script = document.createElement("script");
  script.id = id;
  script.type = "application/ld+json";
  script.text = JSON.stringify(data);
  document.head.appendChild(script);
}

/** Apply document head tags for landing and marketing pages. */
export function applyMarketingSeo({
  title = DEFAULT_SEO.title,
  description = DEFAULT_SEO.description,
  keywords = DEFAULT_SEO.keywords,
  canonical = SITE_URL,
  ogType = "website",
  ogImage = OG_IMAGE,
  jsonLdBlocks = [],
} = {}) {
  document.title = title;
  upsertMeta("name", "description", description);
  upsertMeta("name", "keywords", keywords);
  upsertMeta("name", "robots", "index, follow, max-image-preview:large");
  upsertMeta("property", "og:title", title);
  upsertMeta("property", "og:description", description);
  upsertMeta("property", "og:type", ogType);
  upsertMeta("property", "og:url", canonical);
  upsertMeta("property", "og:site_name", SITE_NAME);
  upsertMeta("property", "og:image", ogImage);
  upsertMeta("property", "og:locale", "en_SG");
  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("name", "twitter:title", title);
  upsertMeta("name", "twitter:description", description);
  upsertMeta("name", "twitter:image", ogImage);
  upsertLink("canonical", canonical);

  injectJsonLd("aivory-seo-graph", jsonLdBlocks.length === 1 ? jsonLdBlocks[0] : { "@context": "https://schema.org", "@graph": jsonLdBlocks });
}

export function buildOrganizationSchema() {
  return {
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: SITE_NAME,
    legalName: "Aivory Labs",
    url: SITE_URL,
    logo: `${SITE_URL}/favicon.svg`,
    description: DEFAULT_SEO.description,
    areaServed: ["SG", "APAC"],
    address: {
      "@type": "PostalAddress",
      addressCountry: "SG",
    },
    sameAs: [],
  };
}

export function buildWebSiteSchema() {
  return {
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    url: SITE_URL,
    name: SITE_NAME,
    publisher: { "@id": `${SITE_URL}/#organization` },
    inLanguage: "en-SG",
  };
}

export function buildSoftwareApplicationSchema(pricingPlans = []) {
  const offers = (pricingPlans || [])
    .filter((p) => p.annualPriceSgd > 0)
    .map((p) => ({
      "@type": "Offer",
      name: `${SITE_NAME} ${p.name}`,
      price: String(p.annualPriceSgd),
      priceCurrency: "SGD",
      url: `${SITE_URL}/signup`,
      availability: "https://schema.org/InStock",
      priceValidUntil: "2027-12-31",
    }));

  return {
    "@type": "SoftwareApplication",
    "@id": `${SITE_URL}/#software`,
    name: SITE_NAME,
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    url: SITE_URL,
    description:
      "Enterprise event management software for registration, QR check-in, badge printing, communications, analytics, and AI-assisted event planning (AivoryONE).",
    featureList: [
      "Event registration and ticketing",
      "QR check-in and badge printing",
      "Event websites and page builder",
      "Email campaigns and segmentation",
      "Wallet passes",
      "Hybrid event management",
      "AivoryONE AI co-planner",
      "API access and webhooks",
    ],
    offers: offers.length ? offers : undefined,
    provider: { "@id": `${SITE_URL}/#organization` },
  };
}

export function buildFaqSchema(faqItems = []) {
  const items = (faqItems || []).filter((row) => row?.q && row?.a);
  if (!items.length) return null;
  return {
    "@type": "FAQPage",
    "@id": `${SITE_URL}/#faq`,
    mainEntity: items.map((row) => ({
      "@type": "Question",
      name: row.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: row.a,
      },
    })),
  };
}

export function buildLandingJsonLd({ faq, pricingPlans } = {}) {
  const blocks = [
    buildOrganizationSchema(),
    buildWebSiteSchema(),
    buildSoftwareApplicationSchema(pricingPlans),
    buildFaqSchema(faq?.items),
  ].filter(Boolean);
  return blocks;
}
